/**
 * Unified icon set for the new composer editor pages
 * (content / skills / llm questions / user questions).
 *
 * Single source of truth — every icon used for a known editor action lives here.
 *
 * Coloring: every `.svg` uses `currentColor` (fill/stroke) so `color` / theme
 * inheritance works like MUI icons. The wrapper merges a `color`/`htmlColor` prop
 * into inline `style`, and sets `display: inline-block` with default `width`/`height`
 * of `1em` so icon size follows the SVG's computed `font-size` (from CSS on the
 * icon or an ancestor).
 *
 * Sizing: default width/height are `1em`, so the icon tracks `font-size` from CSS
 * (ancestor or a className on the icon). Optional `fontSize` prop sets fixed px:
 * "small" | "medium" | "large" (MUI-style). Pass `width` / `height` to override.
 */
import { ReactComponent as AddIconSvg } from "./add.svg";
import { ReactComponent as ImportIconSvg } from "./import.svg";
import { ReactComponent as UploadSvg } from "./upload.svg";
import { ReactComponent as UploadImageIconSvg } from "./upload-image.svg";
import { ReactComponent as DownloadIconSvg } from "./download.svg";
import { ReactComponent as DeleteIconSvg } from "./delete.svg";
import { ReactComponent as EditIconSvg } from "./edit.svg";
import { ReactComponent as AccessIconSvg } from "./access.svg";
import { ReactComponent as AiGenerationIconSvg } from "./ai-generation.svg";
import { ReactComponent as InfoIconSvg } from "./info.svg";
import { ReactComponent as LessonIconSvg } from "./lesson.svg";
import { ReactComponent as LessonPracticeIconSvg } from "./lesson-practice.svg";
import { ReactComponent as LessonReflectionIconSvg } from "./lesson-reflection.svg";
import { ReactComponent as RoleplayIconSvg } from "./roleplay.svg";
import { ReactComponent as UnitIconSvg } from "./unit.svg";
import { ReactComponent as ExpandIconSvg } from "./expand.svg";
import { ReactComponent as DragIconSvg } from "./drag.svg";
import { ReactComponent as ExportIconSvg } from "./export.svg";
import { ReactComponent as SaveIconSvg } from "./save.svg";
import { ReactComponent as CloseIconSvg } from "./close.svg";
import { ReactComponent as SearchIconSvg } from "./search.svg";
import { ReactComponent as ArrowDropDownIconSvg } from "./arrow-drop-down.svg";
import { ReactComponent as CheckIconSvg } from "./check.svg";
import { ReactComponent as InsertDriveFileIconSvg } from "./insert-drive-file.svg";
import { ReactComponent as ArticleOutlinedIconSvg } from "./article-outlined.svg";
import { ReactComponent as LightbulbOutlinedIconSvg } from "./lightbulb-outlined.svg";
import { ReactComponent as LocalOfferOutlinedIconSvg } from "./local-offer-outlined.svg";
import { ReactComponent as PersonOutlineIconSvg } from "./person-outline.svg";
import { ReactComponent as ChevronLeftIconSvg } from "./chevron-left.svg";
import { ReactComponent as ChevronRightIconSvg } from "./chevron-right.svg";
import { ReactComponent as DriveFileMoveIconSvg } from "./drive-file-move.svg";
import { ReactComponent as ErrorOutlineIconSvg } from "./error-outline.svg";
import { ReactComponent as ChatBubbleOutlineIconSvg } from "./chat-bubble-outline.svg";
import { ReactComponent as BlockIconSvg } from "./block.svg";
import { ReactComponent as VideocamIconSvg } from "./videocam.svg";
import { ReactComponent as SettingsIconSvg } from "./settings.svg";

const FONT_SIZE_PX = {
  small: 20,
  medium: 24,
  large: 28,
};

/**
 * @param {object} props
 * @param {'small'|'medium'|'large'|'inherit'|undefined} props.fontSize
 * @param {string|undefined} props.color – CSS color for `currentColor`
 * @param {string|undefined} props.htmlColor – MUI compatibility (same as color)
 */
const wrapIcon = (SvgComp) => {
  const Icon = ({
    fontSize,
    width,
    height,
    style,
    color,
    htmlColor,
    className,
    sx: _sx,
    ...rest
  }) => {
    const sizePx = fontSize != null ? FONT_SIZE_PX[fontSize] : undefined;
    const fromFontProp =
      typeof sizePx === "number" ? sizePx : "1em";
    const finalWidth = width ?? fromFontProp;
    const finalHeight = height ?? fromFontProp;
    const colorValue = htmlColor ?? color;
    const mergedStyle = {
      display: "inline-block",
      verticalAlign: "middle",
      flexShrink: 0,
      ...(colorValue ? { color: colorValue } : {}),
      ...style,
    };
    return (
      <SvgComp
        className={className}
        width={finalWidth}
        height={finalHeight}
        style={mergedStyle}
        focusable="false"
        aria-hidden="true"
        {...rest}
      />
    );
  };
  Icon.displayName = SvgComp?.name || "ComposerIcon";
  return Icon;
};

export const AddIcon = wrapIcon(AddIconSvg);
export const ImportIcon = wrapIcon(ImportIconSvg);
/** Arrow-up upload (e.g. execute / submit) — not the image picker. */
export const UploadIcon = wrapIcon(UploadSvg);
export const UploadImageIcon = wrapIcon(UploadImageIconSvg);
export const DownloadIcon = wrapIcon(DownloadIconSvg);
export const DeleteIcon = wrapIcon(DeleteIconSvg);
export const EditIcon = wrapIcon(EditIconSvg);
export const AccessIcon = wrapIcon(AccessIconSvg);
export const AiGenerationIcon = wrapIcon(AiGenerationIconSvg);
export const InfoIcon = wrapIcon(InfoIconSvg);
export const LessonIcon = wrapIcon(LessonIconSvg);
export const LessonPracticeIcon = wrapIcon(LessonPracticeIconSvg);
export const LessonReflectionIcon = wrapIcon(LessonReflectionIconSvg);
/** The lesson icon for a `lesson_type` (STANDARD | PRACTICE | REFLECTION); standard by default. */
export const lessonIconForType = (lessonType) =>
  lessonType === "PRACTICE"
    ? LessonPracticeIcon
    : lessonType === "REFLECTION"
      ? LessonReflectionIcon
      : LessonIcon;
export const RoleplayIcon = wrapIcon(RoleplayIconSvg);
export const UnitIcon = wrapIcon(UnitIconSvg);
export const ExpandIcon = wrapIcon(ExpandIconSvg);
export const DragIcon = wrapIcon(DragIconSvg);
export const ExportIcon = wrapIcon(ExportIconSvg);
export const SaveIcon = wrapIcon(SaveIconSvg);
export const CloseIcon = wrapIcon(CloseIconSvg);
export const SearchIcon = wrapIcon(SearchIconSvg);
export const ArrowDropDownIcon = wrapIcon(ArrowDropDownIconSvg);
export const CheckIcon = wrapIcon(CheckIconSvg);
export const InsertDriveFileIcon = wrapIcon(InsertDriveFileIconSvg);
export const ArticleOutlinedIcon = wrapIcon(ArticleOutlinedIconSvg);
export const LightbulbOutlinedIcon = wrapIcon(LightbulbOutlinedIconSvg);
export const LocalOfferOutlinedIcon = wrapIcon(LocalOfferOutlinedIconSvg);
export const PersonOutlineIcon = wrapIcon(PersonOutlineIconSvg);
export const ChevronLeftIcon = wrapIcon(ChevronLeftIconSvg);
export const ChevronRightIcon = wrapIcon(ChevronRightIconSvg);
export const DriveFileMoveIcon = wrapIcon(DriveFileMoveIconSvg);
export const ErrorOutlineIcon = wrapIcon(ErrorOutlineIconSvg);
export const ChatBubbleOutlineIcon = wrapIcon(ChatBubbleOutlineIconSvg);
export const BlockIcon = wrapIcon(BlockIconSvg);
export const VideocamIcon = wrapIcon(VideocamIconSvg);
export const SettingsIcon = wrapIcon(SettingsIconSvg);
/** Same glyph as MUI `People` (reuse `access.svg`). */
export const PeopleIcon = AccessIcon;
